import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams, Link } from "react-router-dom";
import { ArrowLeft, Trash2, Save, Link2, BookmarkPlus, Store } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, money, money2, dateShort } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Field, SectionTitle, SelectionBadge, ProcurementBadge } from "@/components/Bits";
import NotesPanel from "@/components/NotesPanel";
import FilesPanel from "@/components/FilesPanel";
import ClientProductView from "@/pages/ClientProductView";
import ApprovalActions from "@/components/ApprovalActions";
import ProjectNav from "@/components/ProjectNav";
import useUnsavedGuard from "@/hooks/useUnsavedGuard";
import NewVendorDialog from "@/components/NewVendorDialog";
import ProductImagesField from "@/components/ProductImagesField";

const input = "w-full rounded-sm border border-line bg-white px-4 py-2.5 text-sm outline-none transition-colors focus:border-lime";
const SELECTION = ["Considering", "Shortlisted", "Presented", "Awaiting Approval", "Approved", "Revision Requested", "Declined"];
const VIS_FIELDS = [
  ["visible_to_client", "Visible to Client"],
  ["show_name", "Show Product Name"],
  ["show_image", "Show Product Image"],
  ["show_description", "Show Description"],
  ["show_client_price", "Show Client Price"],
  ["show_vendor", "Show Vendor"],
  ["show_url", "Show Product URL"],
  ["show_dimensions", "Show Dimensions"],
  ["show_lead_time", "Show Lead Time"],
  ["allow_comments", "Allow Comments"],
  ["allow_approval", "Allow Approval"],
];

const blank = {
  name: "", primary_image: "", primary_image_file_id: "", image_file_ids: [], images: [], category: "Furniture", subcategory: "", description: "",
  project_id: "", space_id: "", vendor_id: "", brand: "", sku: "", product_url: "", quantity: 1,
  dimensions: "", color_finish: "", material: "", lead_time: "", availability: "", notes: "",
  internal_notes: "", retail_price: 0, trade_cost: 0, sale_price: 0, shipping: 0, freight: 0,
  tax: 0, other_fees: 0, markup_type: "percentage", markup_percentage: 35, markup_amount: 0,
  selection_status: "Considering", is_library_item: false, budget_category: "",
  visibility: { visible_to_client: false, show_name: true, show_image: true, show_description: true, show_client_price: true, show_vendor: false, show_url: false, show_dimensions: true, show_lead_time: true, allow_comments: true, allow_approval: true },
};

function calc(f) {
  const qty = Number(f.quantity) || 1;
  const base = Number(f.sale_price) || Number(f.retail_price) || 0;
  const unit =
    f.markup_type === "percentage" ? base * (1 + (Number(f.markup_percentage) || 0) / 100)
    : f.markup_type === "fixed" ? base + (Number(f.markup_amount) || 0)
    : base;
  const fees = ["shipping", "freight", "tax", "other_fees"].reduce((a, k) => a + (Number(f[k]) || 0), 0);
  const trade = Number(f.trade_cost) || base;
  const clientTotal = unit * qty + fees;
  const cost = trade * qty + fees;
  return {
    client_unit_price: unit, client_total: clientTotal, designer_total_cost: cost,
    estimated_profit: clientTotal - cost,
    margin_percentage: clientTotal ? ((clientTotal - cost) / clientTotal) * 100 : 0,
  };
}

export default function ProductDetail() {
  const { id } = useParams();
  // /products/manual carries no :id param, so a missing id is a brand-new product.
  const isNew = !id || id === "new" || id === "manual";
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { isStaff } = useAuth();
  const [form, setForm] = useState({ ...blank, project_id: params.get("project") || "", space_id: params.get("space") || "", is_library_item: params.get("library") === "1" });
  const [projects, setProjects] = useState([]);
  const [spaces, setSpaces] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [categories, setCategories] = useState([]);
  const [proc, setProc] = useState(null);
  const [mode, setMode] = useState(params.get("link") === "1" ? "link" : "manual");
  const [linkUrl, setLinkUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [section, setSection] = useState("basic");
  const [baseline, setBaseline] = useState(() => JSON.stringify({ ...blank, project_id: params.get("project") || "", space_id: params.get("space") || "", is_library_item: params.get("library") === "1" }));
  const [vendorDialog, setVendorDialog] = useState(false);
  const SECTIONS = [
    ["basic", "Basic Information"],
    ["assignment", "Project Assignment"],
    ["details", "Product Details"],
    ["financials", "Financials"],
  ];

  useEffect(() => {
    api.get("/projects").then((r) => setProjects(r.data)).catch(() => {});
    api.get("/vendors").then((r) => setVendors(r.data)).catch(() => {});
    api.get("/meta/enums").then((r) => setCategories(r.data.categories || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!isNew) {
      api.get(`/products/${id}`)
        .then((r) => {
          const next = { ...blank, ...r.data, visibility: { ...blank.visibility, ...(r.data.visibility || {}) } };
          setForm(next);
          setBaseline(JSON.stringify(next));
          setProc(r.data.procurement || null);
        })
        .catch((e) => toast.error(apiError(e)));
    }
  }, [id, isNew]);

  useEffect(() => {
    if (form.project_id) {
      api.get(`/projects/${form.project_id}/spaces`).then((r) => setSpaces(r.data)).catch(() => setSpaces([]));
    } else setSpaces([]);
  }, [form.project_id]);

  const set = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };
  const setVis = (k) => (e) => setForm((f) => ({ ...f, visibility: { ...f.visibility, [k]: e.target.checked } }));

  const live = useMemo(() => calc(form), [form]);
  const dirty = useMemo(() => JSON.stringify(form) !== baseline, [form, baseline]);
  const guardLeave = useUnsavedGuard(dirty);

  if (!isStaff) return <ClientProductView />;

  const save = async (e) => {
    e?.preventDefault();
    setBusy(true);
    const numeric = ["quantity", "retail_price", "trade_cost", "sale_price", "shipping", "freight", "tax", "other_fees", "markup_percentage", "markup_amount"];
    const payload = { ...form };
    numeric.forEach((k) => (payload[k] = Number(payload[k]) || 0));
    if (!payload.project_id) payload.project_id = null;
    if (!payload.space_id) payload.space_id = null;
    if (!payload.vendor_id) payload.vendor_id = null;
    delete payload.procurement;
    try {
      const { data } = isNew ? await api.post("/products", payload) : await api.put(`/products/${id}`, payload);
      toast.success(isNew ? "Product added" : "Product saved");
      if (isNew) {
        setBaseline(JSON.stringify({ ...form, ...data }));
        navigate(`/products/${data.id}`);
      } else {
        setForm((f) => ({ ...f, ...data }));
        setBaseline(JSON.stringify({ ...form, ...data }));
      }
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const saveFromLink = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/products/from-link", {
        product_url: linkUrl, project_id: form.project_id || null, space_id: form.space_id || null,
        vendor_id: form.vendor_id || null, category: form.category, name: form.name || null,
      });
      toast.success("Product created from link");
      navigate(`/products/${data.id}`);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-10 pb-10" data-testid="product-detail-page">
      <NewVendorDialog
        open={vendorDialog}
        existing={vendors}
        onClose={() => setVendorDialog(false)}
        onCreated={(vendor) => {
          // Everything already typed into the product stays; only the vendor changes.
          setVendors((list) => (list.some((v) => v.id === vendor.id) ? list : [...list, vendor]));
          setForm((f) => ({ ...f, vendor_id: vendor.id }));
        }}
      />
      <ProjectNav
        projectId={form.project_id || null}
        projectName={form.project_name || "Project"}
        activeSection="Selections"
        onLeave={guardLeave}
        trail={[
          ...(form.project_id
            ? [{ label: "Products / Sourcing", to: `/projects/${form.project_id}?tab=Selections` }]
            : [{ label: "Products", to: "/library" }]),
          { label: isNew ? "Add Product" : form.name || "Product" },
        ]}
      />
      {form.project_id && (
        <div className="flex flex-wrap gap-3">
          <Link
            to={`/projects/${form.project_id}?tab=Selections`}
            onClick={guardLeave}
            data-testid="back-to-products"
            className="inline-flex items-center gap-2 rounded-sm border border-line bg-white px-4 py-2 text-xs uppercase tracking-[0.12em] text-stone transition-colors hover:border-lime hover:text-ink"
          >
            <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.5} /> Back to Products
          </Link>
          <Link
            to={`/projects/${form.project_id}`}
            onClick={guardLeave}
            data-testid="back-to-project-overview"
            className="inline-flex items-center gap-2 rounded-sm border border-line bg-white px-4 py-2 text-xs uppercase tracking-[0.12em] text-stone transition-colors hover:border-lime hover:text-ink"
          >
            <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.5} /> Back to Project Overview
          </Link>
        </div>
      )}

      <div className="flex flex-col gap-5 border-b border-line pb-8 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="overline mb-3">{isNew ? "New Selection" : form.category}</div>
          <h1 data-testid="product-title" className="font-serif text-3xl tracking-tight text-ink sm:text-4xl">
            {isNew ? "Add Product" : form.name}
          </h1>
          {!isNew && (
            <div className="mt-4 flex flex-wrap items-center gap-3 text-sm text-stone">
              <SelectionBadge status={form.selection_status} testId="product-selection-badge" />
              {proc && <ProcurementBadge status={proc.status} />}
              {form.project_name && <Link to={`/projects/${form.project_id}`} className="hover:text-lime-dark">{form.project_name}</Link>}
              {form.space_name && <span>· {form.space_name}</span>}
            </div>
          )}
        </div>
        {isStaff && (
          <div className="flex flex-wrap gap-3">
            {isNew && (
              <div className="flex rounded-sm border border-line bg-white">
                <button data-testid="add-mode-manual" onClick={() => setMode("manual")} className={`px-4 py-2.5 text-sm transition-colors ${mode === "manual" ? "bg-[#EDEFC4] text-ink" : "text-stone"}`}>Add Manually</button>
                <button data-testid="add-mode-link" onClick={() => setMode("link")} className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm transition-colors ${mode === "link" ? "bg-[#EDEFC4] text-ink" : "text-stone"}`}>
                  <Link2 className="h-4 w-4" strokeWidth={1.5} /> Add From Link
                </button>
              </div>
            )}
            {!isNew && (
              <>
                <button
                  data-testid="product-save-to-library-btn"
                  onClick={async () => {
                    try {
                      await api.post(`/products/${id}/save-to-library`);
                      toast.success("Saved to product library");
                    } catch (e) { toast.error(apiError(e)); }
                  }}
                  className="inline-flex items-center gap-2 rounded-sm border border-line bg-white px-4 py-2.5 text-sm text-ink transition-colors hover:bg-line/50"
                >
                  <BookmarkPlus className="h-4 w-4" strokeWidth={1.5} /> Save to Library
                </button>
                <button
                  data-testid="product-delete-btn"
                  onClick={async () => {
                    if (!window.confirm("Delete this product?")) return;
                    await api.delete(`/products/${id}`);
                    toast.success("Product deleted");
                    navigate(-1);
                  }}
                  className="inline-flex items-center gap-2 rounded-sm border border-clay bg-white px-4 py-2.5 text-sm text-clay transition-colors hover:bg-[#F6DED6]"
                >
                  <Trash2 className="h-4 w-4" strokeWidth={1.5} /> Delete
                </button>
              </>
            )}
            <button
              data-testid="product-save-btn"
              onClick={mode === "link" && isNew ? saveFromLink : save}
              disabled={busy}
              className="inline-flex items-center gap-2 rounded-sm bg-lime px-5 py-2.5 text-sm font-medium text-ink transition-colors hover:bg-lime-dark disabled:opacity-60"
            >
              <Save className="h-4 w-4" strokeWidth={1.5} /> {busy ? "Saving…" : isNew ? "Create Product" : "Save Changes"}
            </button>
          </div>
        )}
      </div>

      {isNew && mode === "link" && (
        <form onSubmit={saveFromLink} className="card-surface space-y-5 p-7" data-testid="add-from-link-form">
          <SectionTitle>Add From Link</SectionTitle>
          <p className="max-w-2xl text-sm leading-relaxed text-stone">
            Paste a retailer or manufacturer URL. We save the link and create the product record now — name,
            image, price and SKU can be filled in manually, and automatic enrichment can be enabled later.
          </p>
          <div className="grid gap-5 md:grid-cols-2">
            <Field label="Product URL">
              <input data-testid="link-url-input" required value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)} placeholder="https://…" className={input} />
            </Field>
            <Field label="Product Name (optional)">
              <input data-testid="link-name-input" value={form.name} onChange={set("name")} className={input} />
            </Field>
            <Field label="Project">
              <select data-testid="link-project-select" value={form.project_id} onChange={set("project_id")} className={input}>
                <option value="">— None (library) —</option>
                {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </Field>
            <Field label="Space">
              <select data-testid="link-space-select" value={form.space_id} onChange={set("space_id")} className={input}>
                <option value="">— Unassigned —</option>
                {spaces.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </Field>
          </div>
          <button data-testid="link-submit-btn" type="submit" disabled={busy} className="rounded-sm bg-lime px-5 py-2.5 text-sm font-medium text-ink hover:bg-lime-dark">
            {busy ? "Creating…" : "Create From Link"}
          </button>
        </form>
      )}

      {(!isNew || mode === "manual") && (
        <>
        <div className="flex gap-7 overflow-x-auto border-b border-line scroll-thin" data-testid="product-form-sections">
          {SECTIONS.map(([key, label]) => (
            <button
              key={key}
              type="button"
              data-testid={`product-section-${key}`}
              onClick={() => setSection(key)}
              className={`whitespace-nowrap border-b-2 pb-3 text-sm transition-colors ${
                section === key ? "border-lime font-medium text-ink" : "border-transparent text-stone hover:text-ink"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        <form onSubmit={save} className="grid gap-10 lg:grid-cols-[1.5fr_1fr]" data-testid="product-form">
          <div className="space-y-10">
            <section className="card-surface p-7">
              <SectionTitle>{SECTIONS.find(([k]) => k === section)?.[1] || "Basic Information"}</SectionTitle>
              <div className="grid gap-6 sm:grid-cols-2">
                {section === "basic" && (
                  <>
                <Field label="Product Name" className="sm:col-span-2">
                  <input data-testid="product-name-input" required value={form.name} onChange={set("name")} className={input} />
                </Field>
                <Field label="Category">
                  <select data-testid="product-category-select" value={form.category} onChange={set("category")} className={input}>
                    {categories.map((c) => <option key={c}>{c}</option>)}
                  </select>
                </Field>
                <Field label="Subcategory">
                  <input data-testid="product-subcategory-input" value={form.subcategory || ""} onChange={set("subcategory")} className={input} />
                </Field>
                <Field label="Description" className="sm:col-span-2">
                  <textarea data-testid="product-description-input" rows={3} value={form.description || ""} onChange={set("description")} className={input} />
                </Field>
                  </>
                )}
                {section === "assignment" && (
                  <>
                <Field label="Project">
                  <select data-testid="product-project-select" value={form.project_id || ""} onChange={set("project_id")} className={input}>
                    <option value="">— None (library item) —</option>
                    {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </Field>
                <Field label="Space">
                  <select data-testid="product-space-select" value={form.space_id || ""} onChange={set("space_id")} className={input}>
                    <option value="">— Unassigned —</option>
                    {spaces.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </Field>
                <Field label="Vendor">
                  <div className="flex gap-2">
                    <select data-testid="product-vendor-select" value={form.vendor_id || ""} onChange={set("vendor_id")} className={input}>
                      <option value="">— No vendor —</option>
                      {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
                    </select>
                    <button
                      type="button"
                      data-testid="add-new-vendor-btn"
                      onClick={() => setVendorDialog(true)}
                      title="Add a new vendor"
                      className="inline-flex shrink-0 items-center gap-2 rounded-sm border border-line bg-white px-3 py-2.5 text-sm text-ink transition-colors hover:border-lime"
                    >
                      <Store className="h-4 w-4" strokeWidth={1.5} />
                      <span className="hidden sm:inline">Add New</span>
                    </button>
                  </div>
                </Field>
                <Field label="Brand / Manufacturer">
                  <input data-testid="product-brand-input" value={form.brand || ""} onChange={set("brand")} className={input} />
                </Field>
                <Field label="SKU">
                  <input data-testid="product-sku-input" value={form.sku || ""} onChange={set("sku")} className={`num ${input}`} />
                </Field>
                <Field label="Product URL">
                  <input data-testid="product-url-input" value={form.product_url || ""} onChange={set("product_url")} className={input} />
                </Field>
                  </>
                )}
                {section === "details" && (
                  <>
                <Field label="Quantity">
                  <input data-testid="product-quantity-input" type="number" min="1" step="1" value={form.quantity} onChange={set("quantity")} className={`num ${input}`} />
                </Field>
                <Field label="Dimensions">
                  <input data-testid="product-dimensions-input" value={form.dimensions || ""} onChange={set("dimensions")} className={input} />
                </Field>
                <Field label="Color / Finish">
                  <input data-testid="product-color-input" value={form.color_finish || ""} onChange={set("color_finish")} className={input} />
                </Field>
                <Field label="Material">
                  <input data-testid="product-material-input" value={form.material || ""} onChange={set("material")} className={input} />
                </Field>
                <Field label="Lead Time">
                  <input data-testid="product-leadtime-input" value={form.lead_time || ""} onChange={set("lead_time")} className={input} />
                </Field>
                <Field label="Availability">
                  <input data-testid="product-availability-input" value={form.availability || ""} onChange={set("availability")} className={input} />
                </Field>
                <Field label="Notes (client-safe)" className="sm:col-span-2">
                  <textarea data-testid="product-notes-input" rows={2} value={form.notes || ""} onChange={set("notes")} className={input} />
                </Field>
                <Field label="Internal Notes (never shown to clients)" className="sm:col-span-2">
                  <textarea data-testid="product-internal-notes-input" rows={2} value={form.internal_notes || ""} onChange={set("internal_notes")} className={input} />
                </Field>
                  </>
                )}
              </div>
            </section>

            {section === "financials" && (
            <section className="card-surface p-7">
              <SectionTitle>Financials</SectionTitle>
              <div className="grid gap-6 sm:grid-cols-3">
                {[["retail_price", "Retail Price"], ["trade_cost", "Designer / Trade Cost"], ["sale_price", "Sale Price"], ["shipping", "Shipping"], ["freight", "Freight"], ["tax", "Tax"], ["other_fees", "Other Fees"]].map(([k, l]) => (
                  <Field key={k} label={l}>
                    <input data-testid={`product-${k.replace(/_/g, "-")}-input`} type="number" step="0.01" value={form[k]} onChange={set(k)} className={`num ${input}`} />
                  </Field>
                ))}
                <Field label="Markup Type">
                  <select data-testid="product-markup-type-select" value={form.markup_type} onChange={set("markup_type")} className={input}>
                    <option value="percentage">Percentage</option>
                    <option value="fixed">Fixed amount</option>
                    <option value="none">None</option>
                  </select>
                </Field>
                <Field label="Markup %">
                  <input data-testid="product-markup-percentage-input" type="number" step="0.01" value={form.markup_percentage} onChange={set("markup_percentage")} className={`num ${input}`} />
                </Field>
                <Field label="Markup Amount">
                  <input data-testid="product-markup-amount-input" type="number" step="0.01" value={form.markup_amount} onChange={set("markup_amount")} className={`num ${input}`} />
                </Field>
              </div>
              <div className="mt-8 grid gap-6 border-t border-line pt-7 sm:grid-cols-5" data-testid="product-calculations">
                {[
                  ["Client Unit Price", money2(live.client_unit_price), "client-unit-price"],
                  ["Client Total", money2(live.client_total), "client-total"],
                  ["Designer Total Cost", money2(live.designer_total_cost), "designer-total-cost"],
                  ["Estimated Profit", money2(live.estimated_profit), "estimated-profit"],
                  ["Margin %", `${live.margin_percentage.toFixed(2)}%`, "margin-percentage"],
                ].map(([l, v, tid]) => (
                  <div key={l} data-testid={`calc-${tid}`}>
                    <div className="overline">{l}</div>
                    <div className="num mt-2 text-lg text-ink">{v}</div>
                  </div>
                ))}
              </div>
            </section>
            )}

            {!isNew && (
              <>
                <section className="card-surface p-7">
                  <SectionTitle>Product Files</SectionTitle>
                  <FilesPanel projectId={form.project_id || undefined} productId={id} compact />
                </section>
                <section className="card-surface p-7">
                  <NotesPanel entityType="product" entityId={id} projectId={form.project_id} />
                </section>
              </>
            )}
          </div>

          <div className="space-y-10">
            <section className="card-surface p-7">
              <SectionTitle>Imagery</SectionTitle>
              <ProductImagesField
                projectId={form.project_id || null}
                value={form}
                onChange={(patch) => setForm((f) => ({ ...f, ...patch }))}
              />
            </section>

            <section className="card-surface p-7">
              <SectionTitle>Selection Status</SectionTitle>
              {form.changed_since_approval && (
                <div className="mb-5 rounded-sm border border-[#E07A5F] bg-[#F6DED6] px-4 py-3 text-xs text-[#A8492E]" data-testid="changed-since-approval-banner">
                  Price or quantity changed since the client approved this selection.
                  <button
                    type="button"
                    data-testid="request-reapproval-btn"
                    onClick={async () => {
                      try {
                        await api.post(`/products/${id}/request-reapproval`);
                        toast.success("Re-approval requested");
                        setForm((f) => ({ ...f, changed_since_approval: false, selection_status: "Awaiting Approval" }));
                      } catch (e) { toast.error(apiError(e)); }
                    }}
                    className="mt-3 block rounded-sm border border-[#A8492E] px-3 py-1.5 text-[11px] uppercase tracking-[0.15em] hover:bg-white/60"
                  >
                    Request Re-Approval
                  </button>
                </div>
              )}
              {form.approved_at && (
                <div className="mb-5 text-xs text-fog" data-testid="admin-approved-stamp">
                  Approved by {form.approved_by_name || "client"} · {dateShort(form.approved_at)}
                  {form.approved_price != null && ` · ${money2(form.approved_price)}`}
                </div>
              )}
              {form.revision_request && (
                <div className="mb-5 rounded-sm border border-[#E07A5F] bg-[#F6DED6] px-4 py-3 text-xs text-[#A8492E]" data-testid="admin-revision-banner">
                  Alternative requested{form.revision_request.reasons?.length ? ` — ${form.revision_request.reasons.join(", ")}` : ""}
                  {form.revision_request.comment && <span className="mt-1 block">“{form.revision_request.comment}”</span>}
                </div>
              )}
              <select data-testid="product-selection-status-select" value={form.selection_status} onChange={set("selection_status")} className={input}>
                {SELECTION.map((s) => <option key={s}>{s}</option>)}
              </select>
              <p className="mt-3 text-[11px] leading-relaxed text-fog">Design selection status is tracked separately from the procurement lifecycle.</p>
              {proc && (
                <div className="mt-6 border-t border-line pt-5">
                  <div className="overline mb-2">Procurement</div>
                  <ProcurementBadge status={proc.status} />
                  <Link to={`/procurement/${proc.id}`} className="mt-4 block text-xs uppercase tracking-[0.15em] text-stone hover:text-ink">Open procurement record →</Link>
                </div>
              )}
              <Field label="Budget Category" className="mt-6">
                <input data-testid="product-budget-category-input" value={form.budget_category || ""} onChange={set("budget_category")} placeholder={form.category} className={input} />
              </Field>
              <label className="mt-6 flex items-center gap-3 text-sm text-ink">
                <input data-testid="product-library-toggle" type="checkbox" checked={!!form.is_library_item} onChange={set("is_library_item")} className="h-4 w-4 accent-[#BEC221]" />
                Library item (reusable, not project-specific)
              </label>
              {!isNew && (
                <div className="mt-6 border-t border-line pt-5">
                  <div className="overline mb-3">Client conversation</div>
                  <ApprovalActions product={{ ...form, id }} onChanged={() => api.get(`/products/${id}`).then((r) => setForm((f) => ({ ...f, ...r.data })))} compact />
                </div>
              )}
            </section>

            <section className="card-surface p-7" data-testid="client-visibility-section">
              <SectionTitle>Client Visibility</SectionTitle>
              <div className="space-y-3">
                {VIS_FIELDS.map(([k, label]) => (
                  <label key={k} className={`flex items-center justify-between gap-4 border-b border-line pb-3 text-sm last:border-0 ${k === "visible_to_client" ? "font-medium text-ink" : "text-stone"}`}>
                    {label}
                    <input
                      data-testid={`visibility-${k.replace(/_/g, "-")}`}
                      type="checkbox"
                      checked={!!form.visibility[k]}
                      onChange={setVis(k)}
                      className="h-4 w-4 shrink-0 accent-[#BEC221]"
                    />
                  </label>
                ))}
              </div>
              <p className="mt-5 text-[11px] leading-relaxed text-fog">
                Trade cost, designer cost, markup, margin, profit and internal notes are always private and
                enforced server-side — they never become visible to client accounts.
              </p>
            </section>
          </div>
        </form>
        </>
      )}
    </div>
  );
}
